import { getApiBaseUrl, httpWithAuth } from './apiClient';
import { getEntraAccessToken } from './authService';

const API_BASE_URL = getApiBaseUrl();

export interface DesignAnalysis {
  colors: Record<string, string>;
  fonts: Record<string, string>;
  layout?: Record<string, string> | null;
  designNotes?: string | null;
}

export interface DesignUploadResponse {
  theme_id: string;
  theme_name: string;
  analysis: DesignAnalysis;
}

export interface ThemeResponse {
  id: string;
  name: string;
  colors: Record<string, string>;
  fonts: Record<string, string>;
  is_active: boolean;
  created_at: string;
}

export interface ActiveThemeResponse {
  theme_id: string | null;
  theme_name: string | null;
  colors: Record<string, string> | null;
  fonts: Record<string, string> | null;
}

class DesignService {
  /**
   * Upload a slide design image for analysis.
   * Uses FormData for file upload (not JSON).
   */
  async uploadDesign(file: File): Promise<DesignUploadResponse> {
    const token = await getEntraAccessToken();
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/design/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error('UNAUTHORIZED');
      const data = await response.json().catch(() => null);
      const message = data?.detail || `Upload failed: ${response.statusText}`;
      throw new Error(message);
    }

    return await response.json();
  }

  /**
   * Save/update a custom theme after user review.
   */
  async saveTheme(params: {
    theme_id: string;
    name?: string;
    colors?: Record<string, string>;
    fonts?: Record<string, string>;
  }): Promise<{ success: boolean; theme_id: string }> {
    return httpWithAuth<{ success: boolean; theme_id: string }>(
      `/api/design/save`,
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  }

  /**
   * List all custom themes for the current user.
   */
  async listThemes(): Promise<ThemeResponse[]> {
    return httpWithAuth<ThemeResponse[]>(`/api/design/themes`);
  }

  /**
   * Delete a custom theme.
   */
  async deleteTheme(themeId: string): Promise<{ success: boolean }> {
    return httpWithAuth<{ success: boolean }>(
      `/api/design/themes/${themeId}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Activate a custom theme for presentation generation.
   */
  async activateTheme(themeId: string): Promise<{ success: boolean }> {
    return httpWithAuth<{ success: boolean }>(
      `/api/design/themes/${themeId}/activate`,
      { method: 'POST' }
    );
  }

  /**
   * Deactivate custom theme and revert to default.
   */
  async deactivateTheme(): Promise<{ success: boolean }> {
    return httpWithAuth<{ success: boolean }>(
      `/api/design/themes/deactivate`,
      { method: 'POST' }
    );
  }

  /**
   * Get the currently active theme.
   */
  async getActiveTheme(): Promise<ActiveThemeResponse> {
    return httpWithAuth<ActiveThemeResponse>(`/api/design/active`);
  }
}

export const designService = new DesignService();
